import { useState, useEffect } from 'react';
import { Eye, Edit2, Trash2 } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { formatDateTime } from '../utils/dateFormatter';
import Modal from './Modal';
import './BOM.css';

const normalizeFamilyCode = (value?: string | null): string =>
  value ? value.replace(/[-\s]/g, '').toUpperCase() : '';

const getSkuFamilyCode = (sku: string): string => {
  if (!sku) {
    return '';
  }

  const trimmed = sku.trim().toUpperCase().replace(/\s+/g, '');
  if (!trimmed) {
    return '';
  }

  // Detectar si es SKU nuevo (tiene guiones y formato TIPO-COLECCION-METAL-TALLA)
  if (trimmed.includes('-')) {
    const parts = trimmed.split('-');
    // Si tiene al menos 3 partes (TIPO-COLECCION-METAL), usar formato con guiones
    if (parts.length >= 3) {
      const tipo = parts[0] || '';
      const coleccion = parts[1] || '';
      const metal = parts[2] || '';
      
      if (tipo && coleccion && metal) {
        // Retornar TIPO-COLECCION-METAL (11 caracteres con guiones)
        return `${tipo}-${coleccion}-${metal}`;
      }
    }
  }

  // Sistema antiguo: usar 5 caracteres sin guiones
  const withoutDashes = trimmed.replace(/-/g, '');
  if (!withoutDashes) {
    return '';
  }

  const first5Chars = withoutDashes.substring(0, 5);

  if (!trimmed.includes('-')) {
    return first5Chars;
  }

  const parts = trimmed.split('-');
  const firstPart = parts[0];
  if (firstPart.length >= 5) {
    return firstPart.substring(0, 5);
  }

  let result = '';
  for (const part of parts) {
    if (!part) continue;
    if (result.length + part.length <= 5) {
      result += (result ? '-' : '') + part;
    } else {
      const remaining = 5 - result.length;
      if (remaining > 0) {
        result += (result ? '-' : '') + part.substring(0, remaining);
      }
      break;
    }
  }

  return result || first5Chars;
};

interface BOMResult {
  sku: string;
  metal_pieces: Array<{
    metal_piece_id: number;
    cod_pieza: string;
    nombre_pieza: string;
    material_base: 'PLATA' | 'LATON';
    qty: number;
    location_code?: string;
  }>;
  stones: Array<{
    stone_id: number;
    cod_piedra: string;
    nombre_piedra: string;
    qty: number;
  }>;
}

interface PendingSKU {
  sku: string;
  normalized_sku: string;
  total_quantity: number;
  last_detected: string;
  product_name?: string;
}

interface BOMItem {
  item_type: 'METAL' | 'STONE';
  cod_item: string;
  qty: number;
  location_code?: string; // ACABADO_PLATA, ACABADO_PVD, ACABADO_DLC (solo para METAL)
}

interface SearchResult {
  id: number;
  cod_pieza?: string;
  cod_piedra?: string;
  nombre_pieza?: string;
  nombre_piedra?: string;
  material_base?: 'PLATA' | 'LATON';
  type?: string;
}

interface BOMSummary {
  sku: string;
  metal_count: number;
  stone_count: number;
  updated_at?: string | null;
}

interface ImportResult {
  sku: string;
  name: string;
  quantity?: number;
  status: 'OK' | 'AUTO_GENERATED' | 'MISSING';
  components: Array<{
    component_sku: string;
    quantity: number;
  }>;
  familyCode?: string;
}

interface ImportSummary {
  total_skus: number;
  skus_with_bom: number;
  skus_without_bom: number;
  pieces: Array<{
    cod_pieza: string;
    nombre_pieza: string;
    unidades: number;
  }>;
  total_pieces: number;
}

export default function BOM() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [sku, setSku] = useState('');
  const [loading, setLoading] = useState(false);
  const [bom, setBom] = useState<BOMResult | null>(null);
  const [pendingSKUs, setPendingSKUs] = useState<PendingSKU[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState<string | null>(null);
  const [selectedPending, setSelectedPending] = useState<PendingSKU | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchType, setSearchType] = useState<'METAL' | 'STONE'>('METAL');
  const [searching, setSearching] = useState(false);
  const [bomItems, setBomItems] = useState<Map<string, BOMItem[]>>(new Map());
  const [bomSummaries, setBomSummaries] = useState<BOMSummary[]>([]);
  const [bomSummarySearch, setBomSummarySearch] = useState('');
  const [loadingBOMSummaries, setLoadingBOMSummaries] = useState(false);
  const [modalMode, setModalMode] = useState<'assign' | 'edit'>('assign');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [shouldReturnToImportModal, setShouldReturnToImportModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showNewFamilyModal, setShowNewFamilyModal] = useState(false);
  const [newFamilyCode, setNewFamilyCode] = useState('');
  const [showBOMImportModal, setShowBOMImportModal] = useState(false);
  const [bomImportFile, setBomImportFile] = useState<File | null>(null);
  const [importingBOMs, setImportingBOMs] = useState(false);
  const [detectingFromShopify, setDetectingFromShopify] = useState(false);
  const [showViewBOMModal, setShowViewBOMModal] = useState(false);
  const [viewingBOM, setViewingBOM] = useState<BOMResult | null>(null);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });


  const setBOMItemsForSku = (targetSku: string, items: BOMItem[]) => {
    setBomItems((prev) => {
      const next = new Map(prev);
      if (!items.length) {
        next.delete(targetSku);
      } else {
        next.set(targetSku, items);
      }
      return next;
    });
  };

  const closeAssignModal = () => {
    setShowAssignForm(null);
    setSelectedPending(null);
    setModalMode('assign');
    setSearchTerm('');
    setSearchResults([]);
    if (shouldReturnToImportModal && importResults.length > 0) {
      setShowImportModal(true);
    }
    setShouldReturnToImportModal(false);
  };

  const fetchBOMData = async (targetSku: string): Promise<BOMResult> => {
    const response = await api.get(`/bom/sku/${encodeURIComponent(targetSku)}`);
    return response.data;
  };

  const loadExistingBOMs = async (term: string = '') => {
    try {
      setLoadingBOMSummaries(true);
      const params = term.trim() ? { search: term.trim() } : {};
      const response = await api.get('/bom', { params });
      setBomSummaries(response.data);
    } catch (err) {
      // Error silencioso - no crítico para la funcionalidad
    } finally {
      setLoadingBOMSummaries(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      showError('Por favor selecciona un archivo CSV');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      setLoading(true);
      setImportResults([]);

      const response = await api.post('/bom/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 segundos de timeout
      });
      
      if (!Array.isArray(response.data)) {
        showError('El servidor devolvió un formato de datos inesperado');
        return;
      }

      const enrichedResults: ImportResult[] = response.data.map((r: ImportResult) => ({
        ...r,
        familyCode: getSkuFamilyCode(r.sku),
      }));

      setImportResults(enrichedResults);
      
      const withBOM = enrichedResults.filter((r) => r.status === 'OK').length;
      const missingBOM = enrichedResults.filter((r) => r.status === 'MISSING').length;
      const autoGenerated = enrichedResults.filter((r) => r.status === 'AUTO_GENERATED').length;

      if (autoGenerated > 0) {
        loadExistingBOMs();
      }

      if (missingBOM > 0) {
        showSuccess(`Análisis completado: ${withBOM} con BOM, ${missingBOM} sin BOM. Puedes continuar sin definir todos los SKUs.`);
      } else {
        showSuccess(`Análisis completado: ${response.data.length} productos procesados, todos tienen BOM`);
      }
    } catch (err: any) {
      let errorMessage = 'Error al importar productos';
      
      if (err.response) {
        errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
      } else if (err.request) {
        errorMessage = 'No se recibió respuesta del servidor. Verifica que el servidor esté funcionando.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (importResults.length === 0) {
      showError('No hay resultados para generar el resumen');
      return;
    }

    try {
      setLoadingSummary(true);
      const response = await api.post('/bom/import-summary', { results: importResults });
      setImportSummary(response.data);
      setShowSummaryModal(true);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Error al generar el resumen');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleExportSummary = () => {
    if (!importSummary) return;

    let csv = '\uFEFFCod.Pieza,Nombre Pieza,Unidades\n';
    
    for (const piece of importSummary.pieces) {
      const codPieza = `"${piece.cod_pieza.replace(/"/g, '""')}"`;
      const nombrePieza = `"${piece.nombre_pieza.replace(/"/g, '""')}"`;
      csv += `${codPieza},${nombrePieza},${piece.unidades}\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `resumen_importacion_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showSuccess('Resumen exportado correctamente');
  };

  const handleExportBOMsCSV = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bom/export/csv', {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `boms_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('BOMs exportados correctamente a CSV');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Error al exportar BOMs a CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleBOMImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setBomImportFile(e.target.files[0]);
    }
  };

  const handleImportBOMsCSV = async () => {
    if (!bomImportFile) {
      showError('Por favor selecciona un archivo CSV');
      return;
    }

    try {
      setImportingBOMs(true);
      const formData = new FormData();
      formData.append('file', bomImportFile);

      const response = await api.post('/bom/import/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSuccess(
        `Familias importadas correctamente: ${response.data.processed} familias procesadas ` +
        `(${response.data.created} nuevas, ${response.data.updated} actualizadas)`
      );

      // Recargar datos
      loadPendingSKUs();
      await loadExistingBOMs(bomSummarySearch);
      setShowBOMImportModal(false);
      setBomImportFile(null);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessage = Array.isArray(errors) 
          ? `Errores de validación:\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n... y ${errors.length - 10} más` : ''}`
          : err.response.data.message || 'Error al importar BOMs desde CSV';
        showError(errorMessage);
      } else {
        showError(err.response?.data?.message || 'Error al importar BOMs desde CSV');
      }
    } finally {
      setImportingBOMs(false);
    }
  };


  const handleDetectFromShopify = async () => {
    if (!confirm('¿Deseas detectar SKUs sin BOM desde Shopify? Esto analizará las ventas de los últimos 7 días.')) {
      return;
    }

    try {
      setDetectingFromShopify(true);
      const response = await api.post('/bom/detect-from-shopify');

      if (response.data.success) {
        showSuccess(
          `Detección completada: ${response.data.familiesAdded} familias sin BOM detectadas. ` +
          `Procesadas ${response.data.ordersProcessed} órdenes, ${response.data.itemsProcessed} items analizados.`
        );
        // Recargar SKUs pendientes
        loadPendingSKUs();
      } else {
        showError(response.data.message || 'Error en la detección');
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Error al detectar SKUs sin BOM desde Shopify');
    } finally {
      setDetectingFromShopify(false);
    }
  };

  const handleSearch = async () => {
    const targetSku = sku.trim();
    if (!targetSku) {
      showError('Por favor ingresa un SKU');
      return;
    }

    try {
      setLoading(true);
      const data = await fetchBOMData(targetSku);
      setBom(data);
      setSku(targetSku);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al obtener BOM');
      setBom(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingSKUs = async () => {
    try {
      setLoadingPending(true);
      const response = await api.get('/bom/pending');
      setPendingSKUs(response.data);
    } catch (err: any) {
      // Error silencioso - no crítico para la funcionalidad
    } finally {
      setLoadingPending(false);
    }
  };

  const handleClearPendingSKUs = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Vaciar Familias Pendientes',
      message: '¿Estás seguro de que quieres vaciar la tabla de familias SKU pendientes? Esta acción no se puede deshacer.',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await api.delete('/bom/pending');
          showSuccess(`Familias pendientes vaciadas: ${response.data.deleted || 0} registros eliminados`);
          await loadPendingSKUs();
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          showError(err.response?.data?.message || 'Error al vaciar la tabla de familias pendientes');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleClearRecognizedFamilies = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Vaciar Familias Reconocidas',
      message: '¿Estás seguro de que quieres vaciar todas las familias SKU reconocidas? Esto eliminará todos los BOMs definidos. Esta acción no se puede deshacer.',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await api.delete('/bom/all');
          showSuccess(
            `Familias reconocidas vaciadas: ${response.data.deleted_families || 0} familias, ${response.data.deleted_items || 0} items eliminados`
          );
          await loadExistingBOMs(bomSummarySearch);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          showError(err.response?.data?.message || 'Error al vaciar las familias reconocidas');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleClearAllFamilies = () => {
    setConfirmModal({
      isOpen: true,
      title: '⚠️ Vaciar Todas las Familias',
      message: '¿Estás seguro de que quieres vaciar TODAS las familias (reconocidas y pendientes)?\n\nEsto eliminará:\n- Todas las familias SKU reconocidas con sus BOMs\n- Todas las familias SKU pendientes\n\n⚠️ Esta acción NO se puede deshacer.',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await api.delete('/bom/all-families');
          showSuccess(
            `Todas las familias vaciadas: ${response.data.deleted_recognized_families || 0} reconocidas (${response.data.deleted_recognized_items || 0} items), ${response.data.deleted_pending_families || 0} pendientes`
          );
          await loadPendingSKUs();
          await loadExistingBOMs(bomSummarySearch);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          showError(err.response?.data?.message || 'Error al vaciar todas las familias');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handlePopulateBOMsFromMetal = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Poblar desde BOM METAL',
      message: '¿Estás seguro de que quieres poblar los BOMs desde la tabla bom_metal? Esto reemplazará los BOMs existentes para las familias encontradas.\n\n⚠️ IMPORTANTE: Asegúrate de haber importado primero el CSV BOM METAL desde Settings.',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await api.post('/bom/populate-from-metal');
          
          if (response.data.items_inserted === 0) {
            showError('No se encontraron datos en bom_metal. Por favor, primero importa el CSV BOM METAL desde Settings → "Importar BOM METAL CSV"');
            return;
          }
          
          showSuccess(
            `BOMs poblados correctamente: ${response.data.families_created} familias creadas, ${response.data.items_inserted} items insertados`
          );
          
          await loadExistingBOMs(bomSummarySearch);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Error al poblar BOMs desde bom_metal';
          showError(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    loadPendingSKUs();
  }, []);

  useEffect(() => {
    loadExistingBOMs();
  }, []);

  // Escuchar evento para abrir modal de asignación desde otros componentes (ej: Settings)
  useEffect(() => {
    const handleOpenBOMAssign = (event: CustomEvent) => {
      const { familyCode } = event.detail;
      if (familyCode) {
        setShowAssignForm(familyCode);
        setSku(familyCode);
        setModalMode('assign');
        setSelectedPending(null);
        setBomItems(new Map());
      }
    };

    window.addEventListener('openBOMAssign', handleOpenBOMAssign as EventListener);
    return () => {
      window.removeEventListener('openBOMAssign', handleOpenBOMAssign as EventListener);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadExistingBOMs(bomSummarySearch);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [bomSummarySearch]);

  const handleSearchItems = async (term: string, type: 'METAL' | 'STONE') => {
    if (!term || term.trim().length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const endpoint = type === 'METAL' ? '/bom/search/metal-pieces' : '/bom/search/stones';
      const response = await api.get(endpoint, { params: { q: term } });
      setSearchResults(response.data || []);
    } catch (err: any) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim().length >= 1 && showAssignForm) {
      const timeoutId = setTimeout(() => {
        handleSearchItems(searchTerm, searchType);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, searchType, showAssignForm]);

  const handleViewBOM = async (targetSku: string) => {
    try {
      setLoading(true);
      const data = await fetchBOMData(targetSku);
      setViewingBOM(data);
      setShowViewBOMModal(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al obtener BOM');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (targetSku: string) => {
    const trimmedSku = targetSku.trim();
    try {
      setLoading(true);
      const data = await fetchBOMData(trimmedSku);
      setBom(data);
      const items: BOMItem[] = [
        ...data.metal_pieces.map((mp) => ({
          item_type: 'METAL' as const,
          cod_item: mp.cod_pieza,
          qty: mp.qty,
          location_code: mp.location_code,
        })),
        ...data.stones.map((stone) => ({
          item_type: 'STONE' as const,
          cod_item: stone.cod_piedra,
          qty: stone.qty,
        })),
      ];
      setBOMItemsForSku(trimmedSku, items);
      setModalMode('edit');
      setShowAssignForm(trimmedSku);
      setSelectedPending(null);
      setSearchTerm('');
      setSearchResults([]);
      setSku(trimmedSku);
      setShouldReturnToImportModal(false);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al preparar la edición');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBOM = (targetSku: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar BOM',
      message: `¿Eliminar el BOM del SKU ${targetSku}?`,
      onConfirm: async () => {
        try {
          await api.delete(`/bom/sku/${encodeURIComponent(targetSku)}`);
          showSuccess(`BOM eliminado para ${targetSku}`);
          if (bom?.sku === targetSku) {
            setBom(null);
          }
          loadExistingBOMs(bomSummarySearch);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          showError(err.response?.data?.error || 'Error al eliminar BOM');
        }
      }
    });
  };

  const handleSaveBOM = async (skuToSave: string) => {
    const trimmedSku = skuToSave.trim();
    if (!trimmedSku) {
      showError('SKU es requerido');
      return;
    }

    const items = bomItems.get(trimmedSku);
    if (!items || items.length === 0) {
      showError('Debe agregar al menos un item al BOM');
      return;
    }

    try {
      setLoading(true);

      if (modalMode === 'edit') {
        await api.put(`/bom/sku/${encodeURIComponent(trimmedSku)}`, { items });
        showSuccess(`BOM actualizado correctamente para la familia ${trimmedSku}`);
      } else {
        await api.post('/bom/assign', { sku: trimmedSku, items });
        showSuccess(`BOM asignado correctamente a la familia ${trimmedSku}`);
      }

      loadPendingSKUs();
      await loadExistingBOMs(bomSummarySearch);

      if (sku.trim() && sku.trim() === trimmedSku) {
        try {
          const data = await fetchBOMData(trimmedSku);
          setBom(data);
        } catch {
          /* ignore refresh errors */
        }
      }

      const savedFamilyNormalized = normalizeFamilyCode(trimmedSku);
      if (savedFamilyNormalized) {
        setImportResults((prev) =>
          prev.filter(
            (res) =>
              normalizeFamilyCode(res.familyCode || getSkuFamilyCode(res.sku)) !== savedFamilyNormalized
          )
        );
      }

      setBOMItemsForSku(trimmedSku, []);
      closeAssignModal();
    } catch (err: any) {
      showError(
        err.response?.data?.error ||
        (modalMode === 'edit' ? 'Error al actualizar BOM' : 'Error al asignar BOM')
      );
    } finally {
      setLoading(false);
    }
  };

  const addBOMItem = (targetSku: string, item: BOMItem) => {
    const current = bomItems.get(targetSku) || [];
    setBOMItemsForSku(targetSku, [...current, item]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const removeBOMItem = (targetSku: string, index: number) => {
    const current = bomItems.get(targetSku) || [];
    const updated = current.filter((_, i) => i !== index);
    setBOMItemsForSku(targetSku, updated);
  };

  const currentBOMItems = showAssignForm ? bomItems.get(showAssignForm) || [] : [];

  return (
    <div className="tab-content">
      <div className="bom-header">
        <h2>Gestión de Despiece (BOM)</h2>
      </div>

      {/* Sección 1: Familias SKU Reconocidas */}
      <div style={{ marginBottom: '3rem' }}>
        <div className="bom-section-header" style={{ 
          padding: '1rem', 
          backgroundColor: 'rgba(34, 197, 94, 0.1)', 
          borderRadius: '8px', 
          border: '1px solid rgba(34, 197, 94, 0.3)',
          marginBottom: '1rem'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--success)' }}>✅ Familias SKU Reconocidas</h3>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Familias de SKU que tienen BOM definido. Puedes importar familias en lote desde CSV o gestionarlas individualmente.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={() => setShowBOMImportModal(true)}
            >
              📤 Importar CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExportBOMsCSV}
              disabled={loading}
            >
              {loading ? '⏳ Exportando...' : '📥 Exportar CSV'}
            </button>
            <button
              className="btn btn-small btn-primary"
              onClick={() => {
                setNewFamilyCode('');
                setShowNewFamilyModal(true);
              }}
              disabled={loading}
              title="Crear una nueva familia manualmente"
            >
              ➕ Añadir Familia
            </button>
            <button
              className="btn btn-small"
              onClick={() => loadExistingBOMs(bomSummarySearch)}
              disabled={loadingBOMSummaries}
            >
              {loadingBOMSummaries ? 'Cargando...' : '🔄 Actualizar'}
            </button>
            <button
              className="btn btn-small btn-danger"
              onClick={handleClearRecognizedFamilies}
              disabled={loading}
              title="Vaciar todas las familias reconocidas"
            >
              🗑️ Vaciar Reconocidas
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="input"
            placeholder="Buscar familia SKU..."
            value={bomSummarySearch}
            onChange={(e) => setBomSummarySearch(e.target.value)}
            style={{ maxWidth: '300px' }}
          />
        </div>

        {loadingBOMSummaries ? (
          <div className="table-container" style={{ padding: '2rem', textAlign: 'center' }}>
            Cargando familias reconocidas...
          </div>
        ) : bomSummaries.length === 0 ? (
          <div className="table-container" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No hay familias reconocidas. Importa un CSV o crea una familia manualmente.
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>SKU Familia</th>
                  <th>Piezas Metálicas</th>
                  <th>Piedras</th>
                  <th>Última actualización</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {bomSummaries.map((summary) => (
                  <tr key={summary.sku}>
                    <td><strong>{summary.sku}</strong></td>
                    <td>{summary.metal_count}</td>
                    <td>{summary.stone_count}</td>
                    <td>
                      {summary.updated_at
                        ? formatDateTime(summary.updated_at)
                        : <span style={{ color: 'var(--text-secondary)' }}>Sin registros</span>}
                    </td>
                    <td>
                      <div className="bom-table-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          className="btn btn-small" 
                          onClick={() => handleViewBOM(summary.sku)}
                          title="Ver BOM"
                          style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Eye size={16} />
                        </button>
                        {canEdit('bom') && (
                          <>
                            <button
                              className="btn btn-small btn-primary"
                              onClick={() => handleOpenEditModal(summary.sku)}
                              title="Editar BOM"
                              style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteBOM(summary.sku)}
                              title="Eliminar BOM"
                              style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sección 2: Familias SKU Pendientes */}
      <div style={{ marginBottom: '3rem' }}>
        <div className="bom-section-header" style={{ 
          padding: '1rem', 
          backgroundColor: 'rgba(255, 193, 7, 0.1)', 
          borderRadius: '8px', 
          border: '1px solid rgba(255, 193, 7, 0.3)',
          marginBottom: '1rem'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--warning)' }}>⚠️ Familias SKU Pendientes</h3>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Familias de SKU que aparecieron en ventas pero aún no tienen BOM asignado. 
              Asigna un BOM para que el sistema pueda procesar las ventas y actualizar el stock automáticamente.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-small btn-primary" onClick={loadPendingSKUs} disabled={loadingPending}>
              {loadingPending ? 'Cargando...' : '🔄 Actualizar'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleDetectFromShopify}
              disabled={detectingFromShopify}
              title="Detectar SKUs sin BOM desde Shopify (últimos 7 días, precio >= $100)"
            >
              {detectingFromShopify ? '⏳ Detectando...' : '🔍 Detectar desde Shopify'}
            </button>
            <button
              className="btn"
              onClick={() => setShowImportModal(true)}
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              title="Analizar productos de ventas y detectar BOMs faltantes"
            >
              🔍 Analizar Productos CSV
            </button>
            <button
              className="btn btn-small btn-danger"
              onClick={handleClearPendingSKUs}
              disabled={loading}
              title="Vaciar tabla de familias pendientes"
            >
              🗑️ Vaciar Pendientes
            </button>
          </div>
        </div>

        {loadingPending ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Cargando familias pendientes...
          </div>
        ) : pendingSKUs.length === 0 ? (
          <div className="table-container" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            ✅ No hay familias pendientes. Todas las familias tienen BOM definido.
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código Familia</th>
                  <th>Nombre Comercial</th>
                  <th>Cantidad Detectada</th>
                  <th>Última Detección</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pendingSKUs.map((pending) => (
                  <tr key={pending.sku}>
                    <td><strong>{pending.sku}</strong></td>
                    <td>{pending.product_name || <span style={{ color: 'var(--text-secondary)' }}>Sin nombre</span>}</td>
                    <td>{pending.total_quantity}</td>
                    <td>{pending.last_detected}</td>
                    <td>
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => {
                          const familyCode = pending.sku;
                          setShouldReturnToImportModal(false);
                          setShowAssignForm(familyCode);
                          setSelectedPending(pending);
                          setSku(familyCode);
                        }}
                      >
                        Asignar BOM
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Búsqueda individual de BOM */}
      <div className="table-container bom-search-section" style={{ marginBottom: '2rem' }}>
        <div className="input-group">
          <label className="input-label">Buscar BOM por SKU Individual</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="input"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Ej: BRA-ESS1-BG, E06GO, RIN-KUD1-BG-085"
              disabled={loading}
            />
            <button
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={loading || !sku.trim()}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
      </div>

      {/* Resultado de búsqueda individual */}
      {bom && (
        <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <h3>BOM para SKU: {bom.sku}</h3>

          {bom.metal_pieces.length > 0 && (
            <div className="table-container" style={{ marginTop: '1rem' }}>
              <h4 style={{ marginBottom: '1rem' }}>Piezas Metálicas</h4>
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Ubicación</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {bom.metal_pieces.map((mp) => (
                    <tr key={mp.metal_piece_id}>
                      <td>
                        <strong
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: mp.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                            color: mp.material_base === 'LATON' ? '#78350f' : '#1f2937',
                          }}
                        >
                          {mp.cod_pieza}
                        </strong>
                      </td>
                      <td>{mp.nombre_pieza}</td>
                      <td>{mp.location_code || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No especificada</span>}</td>
                      <td><strong>{mp.qty}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {bom.stones.length > 0 && (
            <div className="table-container" style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '1rem' }}>Piedras</h4>
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {bom.stones.map((s) => (
                    <tr key={s.stone_id}>
                      <td>{s.cod_piedra}</td>
                      <td>{s.nombre_piedra}</td>
                      <td><strong>{s.qty}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {bom.metal_pieces.length === 0 && bom.stones.length === 0 && (
            <div style={{ marginTop: '1rem', padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No se encontraron componentes para este SKU
            </div>
          )}
        </div>
      )}

      {/* Botones de administración globales (ocultos en sección inferior) */}
      <div style={{ marginTop: '3rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '1rem' }}>
            ⚙️ Opciones Avanzadas
          </summary>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button
              className="btn btn-small"
              onClick={handlePopulateBOMsFromMetal}
              disabled={loading}
              title="Poblar BOMs desde la tabla bom_metal (después de importar BOM METAL CSV)"
            >
              📥 Poblar desde BOM METAL
            </button>
            <button
              className="btn btn-small"
              onClick={handleClearAllFamilies}
              style={{ backgroundColor: 'var(--error-color, #dc3545)', color: 'white' }}
              title="Vaciar todas las familias (reconocidas y pendientes)"
              disabled={loading}
            >
              🗑️ Vaciar Todas las Familias
            </button>
          </div>
        </details>
      </div>

      <Modal
        isOpen={showImportModal}
        onClose={() => {
            setShowImportModal(false);
            setImportResults([]);
            setImportFile(null);
            setShouldReturnToImportModal(false);
        }}
        title="Importar Productos y Resolver BOMs"
        size="large"
        footer={
            importResults.length > 0 && (
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleGenerateSummary}
                    disabled={loadingSummary || importResults.length === 0}
                  >
                    {loadingSummary ? 'Generando resumen...' : 'Continuar y Ver Resumen'}
                  </button>
                </div>
            )
        }
      >
          <div className="bom-import-area">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="bom-import-file-input"
              />
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={!importFile || loading}
              >
                {loading ? 'Procesando...' : 'Analizar y Cargar'}
              </button>
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Sube un archivo CSV con columnas: SKU, Nombre, Cantidad. El sistema intentará asignar BOMs automáticamente.
              </p>
          </div>
            {importResults.length > 0 && (
              <div>
                <h4>Resultados del Análisis ({importResults.length} productos)</h4>
                <div className="table-container" style={{ marginTop: '1rem' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Nombre</th>
                        <th>Estado BOM</th>
                        <th>Componentes Detectados</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResults.map((res, idx) => (
                        <tr key={idx} style={{ backgroundColor: res.status === 'MISSING' ? 'rgba(255, 0, 0, 0.05)' : 'transparent' }}>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <strong>{res.sku}</strong>
                              {res.familyCode && (
                                <small style={{ color: 'var(--text-secondary)' }}>
                                  Familia: {res.familyCode}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>{res.name}</td>
                          <td>
                            <span className={`bom-status-badge ${
                                res.status === 'OK' ? 'bom-status-ok' : 
                                res.status === 'AUTO_GENERATED' ? 'bom-status-auto' : 
                                'bom-status-pending'
                            }`}>
                              {res.status === 'OK' ? 'DEFINIDO' : res.status === 'AUTO_GENERATED' ? 'AUTO-GENERADO' : 'PENDIENTE'}
                            </span>
                          </td>
                          <td>
                            {res.components.length > 0 ? (
                              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                                {res.components.map((c: any, i: number) => (
                                  <li key={i}>{c.component_sku} (x{c.quantity})</li>
                                ))}
                              </ul>
                            ) : (
                              <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Sin componentes</span>
                            )}
                          </td>
                          <td>
                            {res.status === 'MISSING' && (
                              <button
                                className="btn btn-small btn-primary"
                                onClick={() => {
                                  const familyCode = res.familyCode || getSkuFamilyCode(res.sku);
                                  if (!familyCode) {
                                    showError('No se pudo determinar la familia del SKU');
                                    return;
                                  }
                                  setShouldReturnToImportModal(true);
                                  setShowImportModal(false);
                                  setShowAssignForm(familyCode);
                                  setSku(familyCode);
                                  setModalMode('assign');
                                  setSelectedPending(null);
                                  setBomItems(new Map());
                                }}
                              >
                                Definir
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
      </Modal>

      <Modal
        isOpen={showSummaryModal}
        onClose={() => {
            setShowSummaryModal(false);
            setImportSummary(null);
        }}
        title="Resumen de Importación"
        size="medium"
        footer={
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
              <button
                className="btn"
                onClick={() => {
                  setShowSummaryModal(false);
                  setImportSummary(null);
                }}
              >
                Cerrar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  showSuccess('Importación completada (funcionalidad pendiente de implementar)');
                  setShowSummaryModal(false);
                  setImportSummary(null);
                  setShowImportModal(false);
                  setImportResults([]);
                  setImportFile(null);
                }}
              >
                Confirmar Importación
              </button>
            </div>
        }
      >
          {importSummary && (
            <>
                <div className="bom-summary-stats">
                <p><strong>Total de SKUs:</strong> {importSummary.total_skus}</p>
                <p><strong>SKUs con BOM:</strong> <span style={{ color: 'var(--success)' }}>{importSummary.skus_with_bom}</span></p>
                <p><strong>SKUs sin BOM:</strong> <span style={{ color: 'var(--error)' }}>{importSummary.skus_without_bom}</span></p>
                <p><strong>Total de piezas a consumir:</strong> {importSummary.total_pieces}</p>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                <div className="bom-header" style={{ marginBottom: '1rem' }}>
                    <h4>Detalle de Piezas ({importSummary.pieces.length} tipos diferentes)</h4>
                    <button
                    className="btn btn-small btn-primary"
                    onClick={handleExportSummary}
                    >
                    📥 Exportar a CSV
                    </button>
                </div>
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table">
                    <thead>
                        <tr>
                        <th>Código Pieza</th>
                        <th>Nombre Pieza</th>
                        <th>Unidades</th>
                        </tr>
                    </thead>
                    <tbody>
                        {importSummary.pieces.map((piece, idx) => (
                        <tr key={idx}>
                            <td><strong>{piece.cod_pieza}</strong></td>
                            <td>{piece.nombre_pieza}</td>
                            <td>{piece.unidades}</td>
                        </tr>
                        ))}
                    </tbody>
                    </table>
                </div>
                </div>
            </>
          )}
      </Modal>

      {/* Modal para Importar Familias SKU Reconocidas desde CSV */}
      <Modal
        isOpen={showBOMImportModal}
        onClose={() => {
          setShowBOMImportModal(false);
          setBomImportFile(null);
        }}
        title="Importar Familias SKU Reconocidas desde CSV"
        size="medium"
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
            <button
              className="btn"
              onClick={() => {
                setShowBOMImportModal(false);
                setBomImportFile(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleImportBOMsCSV}
              disabled={!bomImportFile || importingBOMs}
            >
              {importingBOMs ? '⏳ Importando...' : 'Importar Familias'}
            </button>
          </div>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Formato del CSV</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              El archivo CSV debe tener las siguientes columnas (en este orden):
            </p>
            <div style={{ 
              marginTop: '0.5rem', 
              padding: '0.75rem', 
              backgroundColor: 'var(--bg-secondary)', 
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}>
              <div style={{ marginBottom: '0.5rem' }}><strong>sku</strong> - Código de familia del SKU (ej: BRA-ESS1-BG, E06GO)</div>
              <div style={{ marginBottom: '0.5rem' }}><strong>item_type</strong> - Tipo de item: 'METAL' o 'STONE'</div>
              <div style={{ marginBottom: '0.5rem' }}><strong>cod_item</strong> - Código de la pieza metálica o piedra (ej: P1-LAT, P54-PLA)</div>
              <div style={{ marginBottom: '0.5rem' }}><strong>qty</strong> - Cantidad requerida (número)</div>
              <div><strong>location_code</strong> - (Opcional) Código de ubicación para METAL: ACABADO_PLATA, ACABADO_PVD, ACABADO_DLC</div>
            </div>
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem', 
              backgroundColor: 'rgba(34, 197, 94, 0.1)', 
              borderRadius: '4px',
              fontSize: '0.875rem'
            }}>
              <strong>✅ Ejemplo de formato:</strong>
              <pre style={{ 
                marginTop: '0.5rem', 
                padding: '0.5rem', 
                backgroundColor: 'var(--bg-primary)', 
                borderRadius: '4px',
                fontSize: '0.75rem',
                overflow: 'auto'
              }}>
{`sku,item_type,cod_item,qty,location_code
BRA-ESS1-BG,METAL,P1-LAT,1,
BRA-ESS1-GO,METAL,P1-LAT,1,
BRA-KUD1-BG,METAL,P16-LAT,26,`}
              </pre>
            </div>
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem', 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              borderRadius: '4px',
              fontSize: '0.875rem'
            }}>
              <strong>⚠️ Importante:</strong> La importación sobreescribirá los BOMs existentes para las familias incluidas en el CSV y añadirá nuevas familias para SKUs que no existan.
            </div>
          </div>

          <div className="bom-import-area">
            <input
              type="file"
              accept=".csv"
              onChange={handleBOMImportFileChange}
              className="bom-import-file-input"
              disabled={importingBOMs}
            />
            {bomImportFile && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                  <strong>Archivo seleccionado:</strong> {bomImportFile.name}
                </p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Tamaño: {(bomImportFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal>



      <Modal
        isOpen={showNewFamilyModal}
        onClose={() => {
            setShowNewFamilyModal(false);
            setNewFamilyCode('');
        }}
        title="Crear Nueva Familia"
        size="small"
        footer={
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
              <button
                className="btn"
                onClick={() => {
                  setShowNewFamilyModal(false);
                  setNewFamilyCode('');
                }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!newFamilyCode || newFamilyCode.trim().length === 0) {
                    showError('El código de familia no puede estar vacío');
                    return;
                  }

                  setShowNewFamilyModal(false);
                  setShowAssignForm(newFamilyCode);
                  setSku(newFamilyCode);
                  setModalMode('assign');
                  setSelectedPending(null);
                  setBomItems(new Map());
                  setSearchTerm('');
                  setSearchResults([]);
                  setNewFamilyCode('');
                }}
                disabled={!newFamilyCode || newFamilyCode.trim().length === 0}
              >
                Continuar y Definir BOM
              </button>
            </div>
        }
      >
          <div>
              <label className="input-label">
                Código de Familia
              </label>
              <input
                type="text"
                className="input"
                placeholder="Ej: E06GO, A01DL, BRA-ES08, RIN-KUD3"
                value={newFamilyCode}
                onChange={(e) => {
                  // Permitir letras, números y guiones, convertir a mayúsculas
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                  setNewFamilyCode(value);
                }}
                style={{ width: '100%', marginTop: '0.5rem' }}
                maxLength={20}
                autoFocus
              />
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Ingresa el código de familia. Para SKUs antiguos usa 5 caracteres sin guiones (ej: E06GO, A01DL). Para SKUs nuevos usa el formato con guiones: TIPO-COLECCION (ej: BRA-ES08, RIN-KUD3, NEC-SP06, PEN-KUD1).
              </p>
            </div>
      </Modal>

      {/* Modal for BOM Assignment - keeping structure similar due to complexity but using Modal component would be ideal if refactored further */}
      {
        showAssignForm && (
            <div className="bom-modal-overlay">
            <div className="bom-modal-content">
              <button
                className="btn btn-small bom-modal-close"
                onClick={() => {
                  setBomItems(new Map());
                  closeAssignModal();
                }}
              >
                ✕
              </button>

              <h3>Asignar BOM a: {showAssignForm}</h3>
              {selectedPending && (
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  SKU normalizado: <strong>{selectedPending.normalized_sku}</strong> · Cantidad detectada:{' '}
                  <strong>{selectedPending.total_quantity}</strong> · Última detección:{' '}
                  <strong>{selectedPending.last_detected}</strong>
                </p>
              )}
              {!selectedPending && modalMode === 'edit' && (
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Estás editando un BOM existente. Los cambios reemplazarán por completo la definición actual
                  del SKU.
                </p>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <h4>Items del BOM</h4>
                {currentBOMItems.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No hay items asignados aún</p>
                ) : (
                  <table className="table" style={{ marginTop: '1rem' }}>
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Código</th>
                        <th>Ubicación</th>
                        <th>Cantidad</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentBOMItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.item_type}</td>
                          <td>{item.cod_item}</td>
                          <td>
                            {item.item_type === 'METAL' ? (
                              <select
                                className="input"
                                value={item.location_code || ''}
                                onChange={(e) => {
                                  if (!showAssignForm) {
                                    return;
                                  }
                                  const items = [...currentBOMItems];
                                  items[idx] = { ...items[idx], location_code: e.target.value };
                                  setBOMItemsForSku(showAssignForm, items);
                                }}
                                style={{ minWidth: '150px' }}
                              >
                                <option value="">Seleccionar...</option>
                                <option value="ACABADO_PLATA">ACABADO_PLATA</option>
                                <option value="ACABADO_PVD">ACABADO_PVD</option>
                                <option value="ACABADO_DLC">ACABADO_DLC</option>
                              </select>
                            ) : (
                              <span style={{ color: 'var(--text-secondary)' }}>N/A</span>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.qty}
                              onChange={(e) => {
                                if (!showAssignForm) {
                                  return;
                                }
                                const parsed = parseFloat(e.target.value);
                                const qtyValue = Number.isNaN(parsed) ? 0 : parsed;
                                const finalQty = Math.max(0.01, qtyValue);
                                const items = [...currentBOMItems];
                                items[idx] = {
                                  ...items[idx],
                                  qty: parseFloat(finalQty.toFixed(4)),
                                };
                                setBOMItemsForSku(showAssignForm, items);
                              }}
                              style={{ 
                                width: '80px', 
                                padding: '0.25rem',
                                borderColor: item.qty < 0.01 ? 'var(--error)' : undefined
                              }}
                              title={item.qty < 0.01 ? 'La cantidad debe ser al menos 0.01' : undefined}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-small"
                              onClick={() => showAssignForm && removeBOMItem(showAssignForm, idx)}
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bom-add-item-section">
                <h4>Agregar Item al BOM</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="input-label">Tipo de Item</label>
                  <select
                    className="input"
                    value={searchType}
                    onChange={(e) => {
                      setSearchType(e.target.value as 'METAL' | 'STONE');
                      setSearchTerm('');
                      setSearchResults([]);
                    }}
                  >
                    <option value="METAL">Pieza Metálica</option>
                    <option value="STONE">Piedra</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="input-label">Buscar {searchType === 'METAL' ? 'Pieza' : 'Piedra'}</label>
                  <input
                    type="text"
                    className="input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Buscar por código o nombre...`}
                    disabled={searching}
                  />
                  {searching && <small style={{ color: 'var(--text-secondary)' }}>Buscando...</small>}
                </div>

                {searchResults.length > 0 && (
                  <div className="bom-search-results">
                    {searchResults.map((result) => (
                      <div
                        key={result.id}
                        className="bom-search-item"
                        onClick={() => {
                          if (!showAssignForm) {
                            return;
                          }
                          const cod = searchType === 'METAL' ? result.cod_pieza! : result.cod_piedra!;
                          addBOMItem(showAssignForm, {
                            item_type: searchType,
                            cod_item: cod,
                            qty: 1,
                            location_code: searchType === 'METAL' ? undefined : undefined,
                          });
                        }}
                      >
                        <strong>{searchType === 'METAL' ? result.cod_pieza : result.cod_piedra}</strong>
                        <br />
                        <small style={{ color: 'var(--text-secondary)' }}>
                          {searchType === 'METAL' ? result.nombre_pieza : result.nombre_piedra}
                          {searchType === 'METAL' && result.material_base && ` (${result.material_base})`}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const hasInvalidItems = currentBOMItems.some(
                      (item) => item.item_type === 'METAL' && !item.location_code
                    );
                    if (hasInvalidItems) {
                      showError('Todas las piezas metálicas deben tener una ubicación de acabado asignada');
                      return;
                    }
                    if (showAssignForm) {
                      handleSaveBOM(showAssignForm);
                    }
                  }}
                  disabled={currentBOMItems.length === 0 || loading}
                >
                  {loading ? 'Guardando...' : modalMode === 'edit' ? 'Guardar cambios' : 'Asignar BOM'}
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    setBomItems(new Map());
                    closeAssignModal();
                  }}
                  disabled={loading}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal para Ver BOM */}
      <Modal
        isOpen={showViewBOMModal}
        onClose={() => {
          setShowViewBOMModal(false);
          setViewingBOM(null);
        }}
        title={`BOM para SKU: ${viewingBOM?.sku || ''}`}
        size="large"
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
            <button
              className="btn"
              onClick={() => {
                setShowViewBOMModal(false);
                setViewingBOM(null);
              }}
            >
              Cerrar
            </button>
            {viewingBOM && canEdit('bom') && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowViewBOMModal(false);
                  handleOpenEditModal(viewingBOM.sku);
                }}
              >
                Editar BOM
              </button>
            )}
          </div>
        }
      >
        {viewingBOM && (
          <>
            {viewingBOM.metal_pieces.length > 0 && (
              <div className="table-container" style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Piezas Metálicas</h4>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Ubicación</th>
                      <th>Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingBOM.metal_pieces.map((mp) => (
                      <tr key={mp.metal_piece_id}>
                        <td>
                          <strong
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: mp.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                              color: mp.material_base === 'LATON' ? '#78350f' : '#1f2937',
                            }}
                          >
                            {mp.cod_pieza}
                          </strong>
                        </td>
                        <td>{mp.nombre_pieza}</td>
                        <td>{mp.location_code || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No especificada</span>}</td>
                        <td><strong>{mp.qty}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {viewingBOM.stones.length > 0 && (
              <div className="table-container" style={{ marginTop: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Piedras</h4>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingBOM.stones.map((s) => (
                      <tr key={s.stone_id}>
                        <td>{s.cod_piedra}</td>
                        <td>{s.nombre_piedra}</td>
                        <td><strong>{s.qty}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {viewingBOM.metal_pieces.length === 0 && viewingBOM.stones.length === 0 && (
              <div style={{ marginTop: '1rem', padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                No se encontraron componentes para este SKU
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        title={confirmModal.title}
        size="small"
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
            <button
              className="btn"
              onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
              data-modal-default="true"
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={confirmModal.onConfirm}
              autoFocus
            >
              Confirmar
            </button>
          </div>
        }
      >
        <p style={{ whiteSpace: 'pre-wrap' }}>{confirmModal.message}</p>
      </Modal>


    </div >
  );
}
